import React, { useEffect, useState } from "react";
import { Sun, Moon, FileText, ChevronRight, Calendar } from 'lucide-react';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import recapsData from "../utils/recapsData";

const recaps = [...recapsData].sort((a, b) => (a.id < b.id ? 1 : -1));

// Color themes based on month for visual variety
const getMonthTheme = (dateId) => {
  const month = parseInt(dateId.split('-')[1], 10);
  const themes = {
    1: { bg: 'from-blue-500 to-cyan-400', light: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300' },
    2: { bg: 'from-pink-500 to-rose-400', light: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-800', text: 'text-pink-700 dark:text-pink-300' },
    3: { bg: 'from-emerald-500 to-green-400', light: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
    4: { bg: 'from-violet-500 to-purple-400', light: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200 dark:border-violet-800', text: 'text-violet-700 dark:text-violet-300' },
    5: { bg: 'from-amber-500 to-yellow-400', light: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300' },
    6: { bg: 'from-orange-500 to-amber-400', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300' },
    7: { bg: 'from-red-500 to-orange-400', light: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' },
    8: { bg: 'from-rose-500 to-pink-400', light: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-800', text: 'text-rose-700 dark:text-rose-300' },
    9: { bg: 'from-teal-500 to-cyan-400', light: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-800', text: 'text-teal-700 dark:text-teal-300' },
    10: { bg: 'from-orange-600 to-amber-500', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300' },
    11: { bg: 'from-amber-600 to-yellow-500', light: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300' },
    12: { bg: 'from-sky-500 to-blue-400', light: 'bg-sky-50 dark:bg-sky-900/20', border: 'border-sky-200 dark:border-sky-800', text: 'text-sky-700 dark:text-sky-300' },
  };
  return themes[month] || themes[1];
};

// Parse date from recap ID for display
const parseRecapDate = (dateId) => {
  const [year, month, day] = dateId.split('-');
  const date = new Date(year, month - 1, day);
  return {
    month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    day: day,
    year: year,
  };
};

/**
 * RecapsPage - Event Recaps listing
 *
 * Design Philosophy:
 * - Consistent with Leaderboard and Events pages
 * - Light mode default for outdoor viewing
 * - Dark mode toggle for evening use
 * - Card-based layout with hover effects
 */
const RecapsPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else {
      try {
        const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (mediaQuery?.matches) {
          setIsDarkMode(true);
        }
      } catch {
        // Fallback for test environments
      }
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Event Recaps
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    Stories from the Course
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Stats/Info Row */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-forest-50 dark:bg-forest-900/30 border border-forest-200 dark:border-forest-800">
                <FileText className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                <span className="text-sm font-semibold text-forest-700 dark:text-forest-300">
                  {recaps.length} Recap{recaps.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Recaps Grid */}
          {recaps.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                No recaps available yet
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recaps.map((recap) => {
                const theme = getMonthTheme(recap.id);
                const dateInfo = parseRecapDate(recap.id);
                // Extract just the event description from the title (after the date)
                const titleParts = recap.title.split(' - ');
                const eventTitle = titleParts.length > 1 ? titleParts[1] : recap.title;

                return (
                  <a
                    key={recap.id}
                    href={`/recaps/${recap.id}`}
                    className="
                      group block bg-white dark:bg-navy-900
                      rounded-xl shadow-md hover:shadow-xl
                      transition-all duration-200
                      border border-gray-200 dark:border-navy-800
                      hover:border-forest-400 dark:hover:border-forest-600
                      overflow-hidden
                    "
                  >
                    {/* Date Badge Header */}
                    <div className={`bg-gradient-to-r ${theme.bg} p-4 flex items-center justify-between`}>
                      <div className="flex items-center gap-3">
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                          <div className="text-xs font-bold text-white/90">{dateInfo.month}</div>
                          <div className="text-2xl font-bold text-white leading-none">{dateInfo.day}</div>
                        </div>
                        <div className="text-white/80 text-sm font-medium">{dateInfo.year}</div>
                      </div>
                      <ChevronRight className="
                        w-5 h-5 text-white/60
                        group-hover:text-white
                        group-hover:translate-x-1
                        transition-all duration-200
                      " />
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="
                        text-base font-semibold text-gray-900 dark:text-gray-100
                        group-hover:text-forest-700 dark:group-hover:text-forest-400
                        transition-colors duration-200
                        line-clamp-2
                      ">
                        {eventTitle}
                      </h3>
                    </div>

                    {/* Footer */}
                    <div className={`
                      px-4 py-3
                      ${theme.light}
                      border-t ${theme.border}
                    `}>
                      <span className={`
                        text-sm font-medium ${theme.text}
                        group-hover:opacity-80
                      `}>
                        Read Story
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          )}

          {/* Count Footer */}
          {recaps.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-500 dark:text-navy-400">
              Showing {recaps.length} recap{recaps.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RecapsPage;
